import { Flex, Typography, Empty, CheckboxProps, EmptyProps } from "antd";
import { observer } from "mobx-react-lite";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { ColumnType } from "antd/es/table";
import { statusFetching } from "services/authClient/constants/types";
import styles from "./plateList.module.css";
import commonTableStyles from "components/CommonTable/table.module.css";
import React from "react";
import { CustomEmpty } from "ui";

interface PlateListSimpleProps<T, S> {
  store: S & {
    data: T[];
    fetchStatus: statusFetching;
    fetch: (options?: { isSilent?: boolean }) => void;
    pollingInterval: number;
  };
  columns: Array<ColumnType<T>>;
  PlateComponent: React.ComponentType<{
    item: T;
    columns: Array<ColumnType<T>>;
    headerItemComponent?: ReactNode | ((item: T) => ReactNode);
    footerItemComponent?: ReactNode | ((item: T) => ReactNode);
    checkbox?: {
      getCheckboxProps?: (item: T) => CheckboxProps;
    };
  }>;
  updateWithInterval?: boolean;
  emptyProps?: {
    children?: ReactNode | string;
    icon?: ReactNode | string;
    description?: ReactNode | string;
  };
  errorMessage?: string;
  className?: string;
  showHeader?: boolean;
  onRow?: (record: T) => { onClick?: () => void; className?: string };
  headerItemComponent?: ReactNode | ((item: T) => ReactNode);
  footerItemComponent?: ReactNode | ((item: T) => ReactNode | undefined);
  checkbox?: {
    getCheckboxProps?: (item: T) => CheckboxProps & { tooltip?: string };
  };
}

export const PlateListSimple = observer(
  <T, S>({
    store,
    columns,
    PlateComponent,
    updateWithInterval,
    errorMessage,
    className,
    showHeader = false,
    onRow,
    headerItemComponent,
    footerItemComponent,
    checkbox,
    emptyProps,
  }: PlateListSimpleProps<T, S>) => {
    const gridTemplateColumns = columns
      .map((col) => {
        const width = Number(col.width) || 100;
        const baseWidth = 100;
        const ratio = width / baseWidth;
        return `${Math.max(ratio, 0.5)}fr`;
      })
      .join(" ");

    const gridCol = { ["--grid-template-columns" as any]: gridTemplateColumns } as React.CSSProperties;

    const headerContent = useMemo(
      () => (
        <div className={styles.headerRow}>
          <div className={styles.headerContent}>
            {columns.map((column, index) => (
              <div key={column.key || index} className={`${styles.columnSection} ${column.className || ""}`}>
                <span className={commonTableStyles.headerTitle}>
                  {typeof column.title === "string"
                    ? column.title
                    : React.isValidElement(column.title)
                      ? column.title
                      : ""}
                </span>
              </div>
            ))}
          </div>
        </div>
      ),
      [columns]
    );

    const plateContent = useMemo(
      () => (
        <div className={`${styles.plateContainer} ${className || ""}`}>
          <Flex vertical gap="small">
            {store.data.map((item: T, index: number) => {
              const rowProps = onRow ? onRow(item) : {};

              return (
                <div
                  key={index}
                  onClick={rowProps.onClick}
                  className={`${rowProps.className ?? ""} ${rowProps.onClick ? "cursor-pointer" : ""}`}
                >
                  <PlateComponent
                    item={item}
                    columns={columns}
                    headerItemComponent={headerItemComponent}
                    footerItemComponent={footerItemComponent}
                    checkbox={
                      checkbox && {
                        getCheckboxProps: checkbox.getCheckboxProps,
                      }
                    }
                  />
                </div>
              );
            })}
          </Flex>
        </div>
      ),
      [className, columns, store.data, onRow, checkbox, headerItemComponent, footerItemComponent]
    );

    useEffect(() => {
      const fetchInterval = setInterval(() => {
        if (updateWithInterval) {
          store.fetch({ isSilent: true });
        } else {
          clearInterval(fetchInterval);
        }
      }, store.pollingInterval || 30000);

      if (store.fetchStatus === "init") {
        store.fetch();
      }

      return () => clearInterval(fetchInterval);
    }, [store, updateWithInterval]);

    if (store.fetchStatus === "error") {
      return (
        <div className={styles.errorContainer}>
          <Typography.Text type="danger">{errorMessage}</Typography.Text>
        </div>
      );
    }

    if (store.data.length === 0) {
      return (
        <div className={styles.emptyContainer}>
          <CustomEmpty {...emptyProps} />
        </div>
      );
    }

    return (
      <div className={styles.plateWrapper} style={gridCol}>
        {showHeader && headerContent}
        {plateContent}
      </div>
    );
  }
);
